import { MenuIcon } from "lucide-react";
import { Outlet } from "react-router-dom";
import { Drawer } from "vaul";

import { Logo } from "@/components/logo";
import { PrimarySidebar } from "@/components/primary-sidebar";
import { SecondarySidebar } from "@/components/secondary-sidebar";
import { Button } from "@/components/ui/button";
import { Version } from "@/components/version";
import { AnnouncementBanner } from "@/features/announcements/components/announcement-banner";
import { AnnouncementWarning } from "@/features/announcements/components/announcement-warning";
import { useRedirectToCV1 } from "@/features/licensing/components/redirect-to-cv1";
import { StorageCapacityNotice } from "@/features/licensing/components/storage-capacity-notice";
import { cn } from "@/lib/utils";

export default function DashboardLayout() {
  useRedirectToCV1();

  return (
    <>
      {/* Static sidebar for desktop */}
      <div
        className={cn(
          "hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col",
          `!top-[var(--announcements-banner-offset)]`,
        )}
      >
        {/* Sidebar component, swap this element with another sidebar if you like */}
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-card px-6 pb-4">
          <div className="flex h-16 shrink-0 items-center">
            <Logo />
          </div>
          <PrimarySidebar />
          <div className="mt-auto space-y-4">
            <StorageCapacityNotice />
            <SecondarySidebar />
          </div>
        </div>
      </div>
      <AnnouncementBanner />

      <div
        className={cn(
          "relative h-full lg:pl-72 lg:pt-2",
          `mt-[var(--announcements-banner-offset)]`,
        )}
      >
        <div className="flex min-h-full flex-col border-border bg-background bg-dotted bg-right-top bg-no-repeat lg:rounded-tl-lg lg:border-l-2 lg:border-t-2">
          <div className="sticky top-0 z-10 flex h-16 shrink-0 items-center gap-x-4 border-b border-border bg-card px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:hidden lg:px-8">
            <Logo />

            <div className="ml-auto">
              <Drawer.Root direction="left">
                <Drawer.Trigger asChild>
                  <Button variant="ghost" size="icon">
                    <MenuIcon aria-hidden="true" />
                  </Button>
                </Drawer.Trigger>
                <Drawer.Portal>
                  <Drawer.Overlay className="fixed inset-0 bg-foreground/40 dark:bg-background/40" />
                  <Drawer.Content
                    className={cn(
                      "fixed bottom-0 left-0 z-10 flex outline-none",
                      `top-[var(--announcements-banner-offset)]`,
                    )}
                  >
                    <Drawer.Title className="hidden">Menu</Drawer.Title>
                    <Drawer.Description className="hidden">
                      Primary navigation
                    </Drawer.Description>
                    <div className="mb-2 ml-2 mt-2 flex w-[310px] grow flex-col rounded-[16px] bg-card p-5">
                      <div className="mb-4 flex shrink-0 items-center">
                        <Logo />
                      </div>

                      <PrimarySidebar />
                      <div className="mt-auto space-y-4">
                        <StorageCapacityNotice />
                        <SecondarySidebar />
                      </div>
                    </div>
                  </Drawer.Content>
                </Drawer.Portal>
              </Drawer.Root>
            </div>
          </div>
          <main className="pb-6 pt-6 lg:pt-12">
            <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
              <AnnouncementWarning />

              <Outlet />
            </div>
          </main>
          <Version className="mt-auto" />
        </div>
      </div>
    </>
  );
}
